import React, { useState, useRef, useEffect } from 'react';

const STATUS_COLORS = {
  'Pending': { bg: '#fef3c7', color: '#d97706', border: '#fcd34d' },
  'In Transit': { bg: '#dbeafe', color: '#2563eb', border: '#93c5fd' },
  'Arrived': { bg: '#e0e7ff', color: '#4f46e5', border: '#a5b4fc' },
  'Delivered': { bg: '#d1fae5', color: '#059669', border: '#6ee7b7' },
  'Closed': { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
  'Cancelled': { bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' },
  'In Progress': { bg: '#dbeafe', color: '#2563eb', border: '#93c5fd' },
  'Completed': { bg: '#d1fae5', color: '#059669', border: '#6ee7b7' },
};

/**
 * Inline status badge with dropdown selector.
 * Shows current status as a colored badge; click to change.
 *
 * @param {string} value - Current status value
 * @param {string[]} options - Available status options
 * @param {function} onChange - Called with new status value
 */
const StatusSelector = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const current = STATUS_COLORS[value] || STATUS_COLORS['Pending'];

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', marginRight: '8px' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '5px 12px',
          borderRadius: '20px',
          border: `1.5px solid ${current.border}`,
          background: current.bg,
          color: current.color,
          fontSize: '12px',
          fontWeight: 700,
          cursor: 'pointer',
          transition: 'all 0.15s',
          whiteSpace: 'nowrap',
        }}
        title="Click to change status"
      >
        <span style={{
          width: '7px', height: '7px', borderRadius: '50%',
          background: current.color, display: 'inline-block',
        }} />
        {value || 'Pending'}
        <span style={{ fontSize: '10px', marginLeft: '2px' }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 300,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)', marginTop: '4px',
          minWidth: '150px', overflow: 'hidden',
        }}>
          {options.map(opt => {
            const c = STATUS_COLORS[opt] || STATUS_COLORS['Pending'];
            const isActive = opt === value;
            return (
              <button
                key={opt}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  width: '100%', padding: '8px 14px', border: 'none',
                  background: isActive ? '#f8fafc' : '#fff',
                  cursor: 'pointer', fontSize: '13px', fontWeight: isActive ? 700 : 500,
                  color: c.color, textAlign: 'left',
                  borderLeft: isActive ? `3px solid ${c.color}` : '3px solid transparent',
                }}
                onMouseEnter={e => e.target.style.background = '#f8fafc'}
                onMouseLeave={e => e.target.style.background = isActive ? '#f8fafc' : '#fff'}
              >
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: c.color, flexShrink: 0,
                }} />
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StatusSelector;
